import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Box, Button, Text, TableHeader, TableRow, TableCell, TableBody,
} from 'grommet';
import uniqid from 'uniqid';
import Case from 'case';
import { useAppTheme } from '@Components/Context';
import { buildDateTimeLabel } from '@Helpers/util';
import {
  VideoDetails, AppButton, DropButton, FlexibleSelectDropdown,
} from '@Components/Control';
import { ProductFormCheckBoxInput } from '@Components/Partial/Product/NewSearch';
import ListTableControls from '../ListTableControls';
import {
  TableWrapper,
  NoDataPlaceholder,
  tablePadding,
  renderTableHeader,
} from '../../SharedComponents';
import { StyledSVG } from '../../StyledSharedComponents';
import { StyledSeeDetailsButton } from '../../ListSearches/StyledListSearches';
import { StyledDetailTable, StyledDetailTableRow } from '../../Dashboard/StyledDashboard';
import {
  CLAIM_HEADER_MAP,
  CLAIM_STATUS_OPTIONS,
  CLAIM_SORT_OPTIONS,
  CLAIM_DATE_FILTER_OPTIONS,
  SELECTABLE_MONETIZE_STATUSES,
  SELECTABLE_REVERT_STATUSES,
  SELECTABLE_RELEASE_STATUSES,
  SELECTABLE_RELEASE_REVERT_STATUSES,
} from '../constants';

const StyledDropMenu = styled(Button)`
  padding: 0.5rem 0.45rem;
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: var(--app-radius);
  color: ${({ textColor }) => textColor};

  &:hover {
    color: ${({ hoverTextColor }) => hoverTextColor};
  }

  @media (max-width: 768px) {
    padding: 0.75rem 0.45rem;
  }
`;

const ClickableTableCell = ({
  options,
  displayLabel,
  onChange,
  button,
  page,
  isOpen: controlledOpen,
  setIsOpen: setControlledOpen,
}) => {
  const [dropOpen, setDropOpen] = React.useState(false);
  const isOpen = typeof controlledOpen === 'boolean' ? controlledOpen : dropOpen;
  const setIsOpen = setControlledOpen || setDropOpen;

  return (
    <DropButton
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      withCloseDropContent={(onClose) => (
        <Box round="6px" pad="small" background="var(--page-input-bg)">
          {options.map((option) => (
            <StyledDropMenu
              plain
              key={uniqid()}
              label={option.display}
              onClick={() => {
                onChange(option.api);
                onClose();
                setIsOpen(false);
              }}
              textColor="var(--page-input-text)"
              hoverTextColor="var(--button-hover-text)"
              hoverIndicator="var(--button-hover-bg)"
            />
          ))}
        </Box>
      )}
    >
      {(Icon) => (
        <AppButton
          dropDown
          tableButtonStyle
          color={button.tableText}
          fontWeight={600}
          label={displayLabel}
          icon={<Icon color={button.tableIcon} size="0.9rem" />}
          focusHighlight={page.focusHighlight}
          isOpen={isOpen}
        />
      )}
    </DropButton>
  );
};

ClickableTableCell.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    display: PropTypes.string.isRequired,
    api: PropTypes.string.isRequired,
  })).isRequired,
  displayLabel: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  button: PropTypes.object.isRequired,
  page: PropTypes.object.isRequired,
  isOpen: PropTypes.bool,
  setIsOpen: PropTypes.func,
};

const ClaimsTable = ({
  small,
  loading,
  showAssetLabelFilter,
  claimsData,
  companyAssetConfig,
  claimDetailsPath,
  handlePage,
  currentPage,
  onQueryChange,
  onClickExport,
  query,
  submitMonetizationRequest,
  submitClaimReleaseRequest,
  submitRevertReleaseRequest,
  refreshData,
  autoClaimProcessing,
  paymentPending = false,
}) => {
  const [selectedBulkClaimIds, setSelectedBulkClaimIds] = React.useState([]);
  const [selectedRevertClaimIds, setSelectedRevertClaimIds] = React.useState([]);
  const [selectedReleaseClaimIds, setSelectedReleaseClaimIds] = React.useState([]);
  const [selectedReleaseRevertClaimIds, setSelectedReleaseRevertClaimIds] = React.useState([]);
  const { data = [], pagination = {} } = claimsData || {};
  const { totalResults = 0, limit = 25 } = pagination;
  const { theme: { page, button }, icons } = useAppTheme();
  const invitedChannels = (companyAssetConfig || [])
    .filter(({ connectionType }) => connectionType === 'INVITE')
    .map(({ channel }) => channel) || [];
  const assetLabels = (companyAssetConfig || []).map(({ assetLabel }) => assetLabel);

  // Only rows with these statuses are selectable
  const isRowSelectable = (row) => row.videoId
    && (SELECTABLE_MONETIZE_STATUSES.includes(row.status)
    || SELECTABLE_REVERT_STATUSES.includes(row.status)
    || SELECTABLE_RELEASE_STATUSES.includes(row.status)
    || SELECTABLE_RELEASE_REVERT_STATUSES.includes(row.status));
  const isAllSelectable = (row) => row.videoId
    && SELECTABLE_MONETIZE_STATUSES.includes(row.status);

  const allSelectableIds = data.filter(isAllSelectable).map((row) => row.id);
  const allSelected = selectedBulkClaimIds.length === allSelectableIds.length
    && allSelectableIds.length > 0;

  // Get available status options for a claim
  const getMonetizationOptions = (status) => {
    if (SELECTABLE_MONETIZE_STATUSES.includes(status)) {
      return [
        { api: 'MONETIZATION_REQUESTED', display: 'Request monetization' },
        { api: 'TAKEDOWN_REQUESTED', display: 'Request takedown' },
        { api: 'RELEASE_REQUESTED', display: 'Request release' },
      ];
    }
    if (SELECTABLE_REVERT_STATUSES.includes(status)) {
      return [{ api: 'REVERT_REQUESTED', display: 'Revert request' }];
    }
    if (SELECTABLE_RELEASE_STATUSES.includes(status)) {
      return [{ api: 'RELEASE_REQUESTED', display: 'Request release' }];
    }
    if (SELECTABLE_RELEASE_REVERT_STATUSES.includes(status)) {
      return [{ api: 'RELEASE_REVERT_REQUESTED', display: 'Revert request' }];
    }
    return [];
  };

  // Get current status display
  const getStatusDisplay = (status) => {
    if (status === 'TAKENDOWN') return 'Taken down';
    return status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, ' ');
  };

  // Handle row checkbox select
  const handleRowSelect = (claimId, status) => {
    if (SELECTABLE_MONETIZE_STATUSES.includes(status)) {
      setSelectedBulkClaimIds((prev) => (prev.includes(claimId)
        ? prev.filter((id) => (id !== claimId))
        : [...prev, claimId]
      ));
      setSelectedRevertClaimIds((prev) => prev.filter((id) => (id !== claimId)));
      setSelectedReleaseClaimIds((prev) => prev.filter((id) => (id !== claimId)));
    } else if (SELECTABLE_REVERT_STATUSES.includes(status)) {
      setSelectedRevertClaimIds((prev) => (prev.includes(claimId)
        ? prev.filter((id) => (id !== claimId))
        : [...prev, claimId]
      ));
      setSelectedBulkClaimIds((prev) => prev.filter((id) => (id !== claimId)));
      setSelectedReleaseClaimIds((prev) => prev.filter((id) => (id !== claimId)));
    } else if (SELECTABLE_RELEASE_STATUSES.includes(status)) {
      setSelectedReleaseClaimIds((prev) => (prev.includes(claimId)
        ? prev.filter((id) => (id !== claimId))
        : [...prev, claimId]
      ));
      setSelectedBulkClaimIds((prev) => prev.filter((id) => (id !== claimId)));
      setSelectedRevertClaimIds((prev) => prev.filter((id) => (id !== claimId)));
    } else if (SELECTABLE_RELEASE_REVERT_STATUSES.includes(status)) {
      setSelectedReleaseRevertClaimIds((prev) => (prev.includes(claimId)
        ? prev.filter((id) => id !== claimId)
        : [...prev, claimId]
      ));
      setSelectedBulkClaimIds((prev) => prev.filter((id) => id !== claimId));
      setSelectedRevertClaimIds((prev) => prev.filter((id) => id !== claimId));
      setSelectedReleaseClaimIds((prev) => prev.filter((id) => id !== claimId));
    }
  };

  // Handle select all
  const handleSelectAllMonetize = () => {
    if (allSelected) {
      setSelectedBulkClaimIds([]);
      return;
    }

    const eligible = data.filter((dt) => (
      SELECTABLE_MONETIZE_STATUSES.includes(dt.status)
    )).map((dt) => dt.id);
    setSelectedBulkClaimIds(eligible);
  };

  // Configure payload for API
  /* eslint-disable camelcase */
  const getMonetizationPayload = (useData) => {
    const formattedData = useData?.length && useData
      .map((row) => ({
        claim_id: row.id,
        previous_status: row.status,
        asset_id: row.assetId,
        video_title: row.videoTitle,
        video_id: row.videoId,
        video_url: row.videoId ? `https://www.youtube.com/watch?v=${row.videoId}` : null,
        notes: '',
      }));
    return formattedData || [];
  };

  // Handle bulk submit
  const handleBulkMonetizationRequest = (requestedStatus) => {
    const filteredData = data.filter((row) => selectedBulkClaimIds.includes(row.id));
    const claims = getMonetizationPayload(filteredData).map((claim) => ({
      ...claim,
      requested_status: requestedStatus,
    }));
    if (claims.length > 0) {
      submitMonetizationRequest({ claims }, () => refreshData());
      setSelectedBulkClaimIds([]);
    }
  };

  // Handle bulk revert request
  const handleBulkRevertRequest = () => {
    const filteredData = data.filter((row) => selectedRevertClaimIds.includes(row.id));
    const claims = getMonetizationPayload(filteredData).map((claim) => ({
      ...claim,
      requested_status: 'REVERT_REQUESTED',
    }));
    if (claims.length > 0) {
      submitMonetizationRequest({ claims }, () => refreshData(), true);
      setSelectedRevertClaimIds([]);
    }
  };

  // Handle single request for either monetize or revert
  const handleSingleRowRequest = (row, requestedStatus) => {
    if (requestedStatus === 'RELEASE_REVERT_REQUESTED') {
      if (submitRevertReleaseRequest) {
        /* eslint-disable camelcase */
        const body = { claims: [{ claim_id: row.id }] };
        /* eslint-enable camelcase */
        submitRevertReleaseRequest(body, () => refreshData());
        setSelectedReleaseRevertClaimIds((prev) => prev.filter((id) => id !== row.id));
      }
      return;
    }
    if (requestedStatus === 'RELEASE_REQUESTED') {
      if (submitClaimReleaseRequest) {
        /* eslint-disable camelcase */
        const releaseBody = {
          claims: [{
            claim_id: row.id,
            asset_id: row.assetId,
            video_id: row.videoId,
            video_title: row.videoTitle || undefined,
            previous_status: row.status,
          }],
        };
        /* eslint-enable camelcase */
        submitClaimReleaseRequest(releaseBody, () => refreshData());
        setSelectedReleaseClaimIds((prev) => prev.filter((id) => id !== row.id));
      }
      return;
    }
    /* eslint-disable camelcase */
    const claims = getMonetizationPayload([row]).map((claim) => ({
      ...claim,
      requested_status: requestedStatus,
    }));
    /* eslint-enable camelcase */
    if (claims.length > 0) {
      submitMonetizationRequest({ claims }, () => refreshData(), requestedStatus === 'REVERT_REQUESTED');
      setSelectedRevertClaimIds((prev) => prev.filter((id) => id !== row.id));
      setSelectedBulkClaimIds((prev) => prev.filter((id) => id !== row.id));
    }
  };

  // Handle bulk release request
  // ACTIVE claims land in selectedReleaseClaimIds; POTENTIAL claims land in selectedBulkClaimIds
  /* eslint-disable camelcase */
  const handleBulkReleaseRequest = () => {
    if (!submitClaimReleaseRequest) return;
    const releaseIds = selectedReleaseClaimIds.length > 0
      ? selectedReleaseClaimIds
      : selectedBulkClaimIds;
    const filteredData = data.filter((row) => releaseIds.includes(row.id));
    if (filteredData.length === 0) return;
    const releaseBody = {
      claims: filteredData.map((row) => ({
        claim_id: row.id,
        asset_id: row.assetId,
        video_id: row.videoId,
        video_title: row.videoTitle || undefined,
        previous_status: row.status,
      })),
    };
    submitClaimReleaseRequest(releaseBody, () => refreshData());
    setSelectedReleaseClaimIds([]);
    setSelectedBulkClaimIds([]);
  };
  /* eslint-enable camelcase */

  const handleBulkReleaseRevertRequest = () => {
    if (!submitRevertReleaseRequest) return;
    const filteredData = data.filter((row) => selectedReleaseRevertClaimIds.includes(row.id));
    if (filteredData.length === 0) return;
    /* eslint-disable camelcase */
    const body = {
      claims: filteredData.map((row) => ({ claim_id: row.id })),
    };
    /* eslint-enable camelcase */
    submitRevertReleaseRequest(body, () => refreshData());
    setSelectedReleaseRevertClaimIds([]);
  };

  const anySelected = selectedBulkClaimIds.length > 0
    || selectedRevertClaimIds.length > 0
    || selectedReleaseClaimIds.length > 0
    || selectedReleaseRevertClaimIds.length > 0;

  const getBulkActionOptions = () => {
    if (selectedReleaseClaimIds.length > 0) {
      return [{ api: 'RELEASE_REQUESTED', display: 'Request release' }];
    }
    if (selectedRevertClaimIds.length > 0) {
      return [{ api: 'REVERT_REQUESTED', display: 'Revert request' }];
    }
    if (selectedReleaseRevertClaimIds.length > 0) {
      return [{ api: 'RELEASE_REVERT_REQUESTED', display: 'Revert request' }];
    }
    return [
      { api: 'MONETIZATION_REQUESTED', display: 'Request monetization' },
      { api: 'TAKEDOWN_REQUESTED', display: 'Request takedown' },
      { api: 'RELEASE_REQUESTED', display: 'Request release' },
    ];
  };

  const handleBulkAction = (api) => {
    if (api === 'RELEASE_REQUESTED') {
      handleBulkReleaseRequest();
    } else if (api === 'REVERT_REQUESTED') {
      handleBulkRevertRequest();
    } else if (api === 'RELEASE_REVERT_REQUESTED') {
      handleBulkReleaseRevertRequest();
    } else {
      handleBulkMonetizationRequest(api);
    }
  };

  const renderDataPoint = (uid, value, row) => {
    if (uid === 'videoTitle') {
      return (
        <VideoDetails
          key={uniqid()}
          small={small}
          videoTitle={row.videoTitle}
          videoId={row.videoId}
          videoViews={row.engagedViews}
          channelId={row.channelId}
          channelDisplayName={row.channelDisplayName}
          textColor={page.tableText}
          buttonHighlight={button.primaryBg}
        />
      );
    }

    if (uid === 'timeCreated' || uid === 'timeStatusLastModified') {
      return (
        <Text size="1rem" color={page.tableText}>
          {buildDateTimeLabel(value)}
        </Text>
      );
    }

    const displayValue = ['status', 'contentType'].includes(uid)
      ? Case.title(value.replace(/_/g, ' '))
      : value;

    return (
      <Text size="1rem" color={page.tableText}>
        {displayValue}
      </Text>
    );
  };

  const renderActionButton = (row) => (
    <StyledSeeDetailsButton
      plain
      textColor={page.tableText}
      hoverTextColor={page.highlight}
      href={claimDetailsPath(row.id)}
    >
      <Box wrap={false} direction="row" gap="0.5rem" align="center">
        <Text size="1rem" className="seeDetailsText">
          See details
        </Text>
        <Box flex={false} width="1rem" height="1rem">
          <StyledSVG
            $fillColor={page.tableText}
            $hoverColor={page.highlight}
            className="seeDetailsIcon"
            src={icons.seeDetailsIcon}
            width="1rem"
            height="1rem"
            title="See details"
          />
        </Box>
      </Box>
    </StyledSeeDetailsButton>
  );

  // Render conditionally clickable table cell for status
  const renderStatusCell = (row) => {
    const options = getMonetizationOptions(row.status);
    const currentStatus = row.status;

    if (!autoClaimProcessing && options.length > 0 && row.videoId) {
      const selectedOptionObj = options.find((opt) => opt.api === currentStatus);
      const displayLabel = selectedOptionObj
        ? selectedOptionObj.display
        : getStatusDisplay(currentStatus);

      return (
        <ClickableTableCell
          options={options}
          currentStatus={currentStatus}
          displayLabel={displayLabel}
          onChange={(api) => handleSingleRowRequest(row, api)}
          button={button}
          page={page}
        />
      );
    }

    return (
      <Text size="1rem" color={page.tableText}>
        {getStatusDisplay(row.status)}
      </Text>
    );
  };

  const renderTableCell = (uid, row) => {
    if (uid === 'actions') {
      return renderActionButton(row);
    }

    if (uid === 'status') {
      return renderStatusCell(row);
    }

    return renderDataPoint(uid, row[uid], row);
  };

  // Render table header
  const renderTableHeaderRow = () => (
    <TableRow>
      {!small && !autoClaimProcessing && (
        <TableCell key="checkbox-header" scope="col" align="start" size={CLAIM_HEADER_MAP[0].size} style={{ marginLeft: '-11px' }}>
          {allSelectableIds.length > 0 && (
            <ProductFormCheckBoxInput
              value={allSelected}
              handleFormValues={handleSelectAllMonetize}
              borderColor={page.inputBorder}
              checkmarkColor={page.inputIcon}
              focusHighlight={page.focusHighlight}
              customBoxSize="1.25rem"
              customCheckmarkSize="1.1rem"
            />
          )}
        </TableCell>
      )}
      {CLAIM_HEADER_MAP.filter(({ uid }) => uid !== 'checkbox').map(({ uid, display, size }) => (
        <TableCell key={uid} scope="col" align="start" size={size}>
          {renderTableHeader(display, small)}
        </TableCell>
      ))}
    </TableRow>
  );

  const mobileHeaderMap = CLAIM_HEADER_MAP.filter(
    ({ uid }) => uid !== 'checkbox' && uid !== 'status' && uid !== 'actions',
  );

  const renderMobileRow = (row) => (
    <Box
      key={row.id}
      as="tr"
      direction="column"
      border={{
        color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
      }}
      pad={{ vertical: '1rem', horizontal: '0.5rem' }}
      background={page.tableBg}
    >
      {mobileHeaderMap.map(({ uid, display }) => (
        <Box
          key={uid}
          direction="row"
          gap="0.5rem"
          align="center"
          as="td"
          pad={{ vertical: '0.5rem' }}
        >
          <Text size="0.875rem" color={page.tableHeader} weight={500}>
            {display}
            :
          </Text>
          {renderTableCell(uid, row)}
        </Box>
      ))}
      <Box direction="row" justify="between" as="td" align="center">
        <Box direction="row" gap="0.5rem" align="center">
          <Text size="0.875rem" color={page.tableHeader} weight={500}>
            Status:
          </Text>
          {renderStatusCell(row)}
        </Box>
        {renderActionButton(row)}
      </Box>
    </Box>
  );

  const renderTableContents = () => {
    if (loading || !data || data.length === 0) {
      const label = loading
        ? 'Loading claims...'
        : 'No claims found.';
      return (
        <NoDataPlaceholder
          noShadow
          noRound
          showRefreshButton={(
            !loading && (!data || data?.length === 0) && query && Object.keys(query).length > 0
          )}
          refreshQuery={() => onQueryChange('reset')}
          buttonHighlight={button.primaryBg}
          loading={loading || !data}
          label={label}
          noDataLabel="No claims found matching the current filters."
          textColor={page.primaryText}
          loaderColor={page.highlight}
        />
      );
    }

    return (
      <StyledDetailTable>
        {!small && (
          <TableHeader>
            {renderTableHeaderRow()}
          </TableHeader>
        )}
        <TableBody>
          {!small && data.map((row) => (
            <StyledDetailTableRow key={row.id} rowColor={page.tableBg}>
              {!autoClaimProcessing && (
                <TableCell key={`checkbox-${row.id}`} size={CLAIM_HEADER_MAP[0].size} align="center" style={{ marginLeft: '-11px' }}>
                  {isRowSelectable(row) && (
                    <ProductFormCheckBoxInput
                      value={(
                        selectedBulkClaimIds.includes(row.id)
                        || selectedRevertClaimIds.includes(row.id)
                        || selectedReleaseClaimIds.includes(row.id)
                        || selectedReleaseRevertClaimIds.includes(row.id)
                      )}
                      handleFormValues={() => handleRowSelect(row.id, row.status)}
                      borderColor={page.inputBorder}
                      checkmarkColor={page.inputIcon}
                      focusHighlight={page.focusHighlight}
                      customBoxSize="1.25rem"
                      customCheckmarkSize="1.1rem"
                    />
                  )}
                </TableCell>
              )}
              {CLAIM_HEADER_MAP.filter(({ uid }) => uid !== 'checkbox').map(({ uid, size }) => (
                <TableCell key={uid} size={size} align="start">
                  <Box pad={{ vertical: uid === 'videoTitle' ? '0' : '0.5rem' }}>
                    {renderTableCell(uid, row)}
                  </Box>
                </TableCell>
              ))}
            </StyledDetailTableRow>
          ))}
          {small && data.map((row) => renderMobileRow(row))}
        </TableBody>
      </StyledDetailTable>
    );
  };

  return (
    <TableWrapper
      zeroBasedPageIndex
      small={small}
      header="Claims"
      boxPad={tablePadding(small)}
      isPaged={totalResults > limit}
      totalResults={totalResults}
      currentResults={data?.length}
      selectedResultsCount={limit}
      currentPage={currentPage}
      handlePage={handlePage}
      overrideNextPageDisabled={pagination?.skip + pagination?.limit >= pagination?.totalResults}
      tableControls={(
        <Box direction="column" gap="0.5rem" justify="end">
          <ListTableControls
            small={small}
            query={{ ...query, q: query.q || query.channelId || '' }}
            onQueryChange={(key, value) => {
              if (key === 'q') {
                const isChannelId = typeof value === 'string'
                  && value.startsWith('UC')
                  && value.length === 24;
                if (isChannelId) {
                  onQueryChange('channelId', value);
                  onQueryChange('q', '');
                } else {
                  onQueryChange('q', value);
                  onQueryChange('channelId', '');
                }
              } else {
                onQueryChange(key, value);
              }
            }}
            onClickExport={onClickExport}
            showHideCompanyChannelVideos={Boolean(invitedChannels?.length > 0)}
            assetLabels={showAssetLabelFilter ? assetLabels : []}
            searchPlaceholder="Search claims"
            statusOptions={CLAIM_STATUS_OPTIONS}
            statusLabel="All Statuses"
            sortOptions={CLAIM_SORT_OPTIONS}
            sortLabel="Sort By"
            dateFilterOptions={CLAIM_DATE_FILTER_OPTIONS}
            paymentPending={paymentPending}
          />
          {!autoClaimProcessing && (
            <Box direction="row" gap="0.5rem" width="fit-content" alignSelf="end">
              <FlexibleSelectDropdown
                textColor={page.inputText}
                handleItemSelect={handleBulkAction}
                flexibleOptions={getBulkActionOptions()}
                noSelectionLabel="Select a bulk action"
                displayKey="display"
                valueKey="api"
                customPad="0.2rem 0.3rem"
                selectedOption={null}
                withReset={false}
                disabled={!anySelected}
              />
            </Box>
          )}
        </Box>
      )}
    >
      <Box fill="horizontal" direction="column">
        {renderTableContents()}
      </Box>
    </TableWrapper>
  );
};

ClaimsTable.propTypes = {
  small: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  showAssetLabelFilter: PropTypes.bool,
  claimsData: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape({
      assetId: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      contentType: PropTypes.string,
      engagedViews: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      isPartnerUploaded: PropTypes.bool,
      thirdPartyClaim: PropTypes.bool,
      kind: PropTypes.string,
      origin: PropTypes.shape({
        source: PropTypes.string,
      }),
      status: PropTypes.string.isRequired,
      studioInfo: PropTypes.shape({
        claimUrl: PropTypes.string,
        videoUrl: PropTypes.string,
      }),
      timeCreated: PropTypes.string.isRequired,
      timeStatusLastModified: PropTypes.string,
      videoId: PropTypes.string,
      videoTitle: PropTypes.string,
      videoViews: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })).isRequired,
    pagination: PropTypes.shape({
      totalResults: PropTypes.number,
      skip: PropTypes.number,
      limit: PropTypes.number,
    }).isRequired,
  }),
  companyAssetConfig: PropTypes.arrayOf(
    PropTypes.shape({
      channel: PropTypes.string.isRequired,
      assetLabel: PropTypes.string.isRequired,
      connectionType: PropTypes.string.isRequired,
    }),
  ),
  claimDetailsPath: PropTypes.func.isRequired,
  handlePage: PropTypes.func.isRequired,
  currentPage: PropTypes.number.isRequired,
  onQueryChange: PropTypes.func.isRequired,
  onClickExport: PropTypes.func.isRequired,
  query: PropTypes.object.isRequired,
  autoClaimProcessing: PropTypes.bool.isRequired,
  submitMonetizationRequest: PropTypes.func.isRequired,
  submitClaimReleaseRequest: PropTypes.func,
  submitRevertReleaseRequest: PropTypes.func,
  refreshData: PropTypes.func.isRequired,
  paymentPending: PropTypes.bool,
};

export default ClaimsTable;
